import { Box } from '@mui/material';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { PaloPositionColumn, PaloPositionImage, PaloPositionTable } from '@synergy/components-palo';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { getLocalizedString } from '@synergy/utils';
import { tokens } from '@synergy/theme-palo';

import { OrderApprovalItem } from '../models/order-approval';
import { localesSelector } from '../../../common/selectors/locale-selectors';
import { useSelector } from '../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

/** What an approval is about: its positions, by supplier (#2281). */
export const PaloApprovalItems = ({ id, currency }: { id: number; currency: string }): ReactElement => {
    const dispatch = useThunkDispatch();
    const locales = useSelector(localesSelector);
    const [items, setItems] = useState<OrderApprovalItem[]>();

    useEffect(() => {
        let cancelled = false;

        void dispatch(async (_dispatch, _getState, { api }) => {
            const result = await api.orderApprovals.getItems(id);

            if (!cancelled) {
                setItems(result.success ? result.result : []);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [dispatch, id]);

    const columns = useMemo<PaloPositionColumn<OrderApprovalItem>[]>(
        () => [
            {
                key: 'image',
                type: 'icon',
                label: <FormattedMessage id="app.orders.position.image" />,
                render: item => (
                    <PaloPositionImage
                        image={item.previewImage}
                        name={getLocalizedString(locales, item.name)}
                        caption={
                            item.number && (
                                <>
                                    <FormattedMessage id="app.orders.position.number" /> {item.number}
                                </>
                            )
                        }
                    />
                ),
            },
            {
                key: 'supplier',
                type: 'classification',
                label: <FormattedMessage id="app.table.column.supplier" />,
                render: item => item.supplierName ?? '–',
            },
            {
                key: 'number',
                type: 'articleNumber',
                label: <FormattedMessage id="app.orders.position.number" />,
                render: item => item.number ?? '–',
            },
            {
                key: 'name',
                type: 'name',
                label: <FormattedMessage id="app.orders.position.article" />,
                render: item => getLocalizedString(locales, item.name),
            },
            {
                key: 'quantity',
                type: 'quantity',
                label: <FormattedMessage id="app.orders.position.quantity" />,
                render: item => (
                    <>
                        <FormattedNumber value={item.quantity} /> {item.orderUnit}
                    </>
                ),
            },
            {
                key: 'price',
                type: 'price',
                label: <FormattedMessage id="app.orders.position.price" />,
                render: item => <FormattedNumber value={item.netPrice} minimumFractionDigits={2} />,
            },
            {
                key: 'total',
                type: 'amount',
                label: <FormattedMessage id="app.orders.position.total" />,
                render: item => <FormattedNumber value={item.totalNetPrice} minimumFractionDigits={2} />,
            },
        ],
        [locales]
    );

    if (!items) {
        return (
            <Box sx={{ fontSize: '12px', color: tokens.color.ink3 }}>
                <FormattedMessage id="app.orders.detail.loading" />
            </Box>
        );
    }

    return (
        <Box sx={{ overflowX: 'auto', maxWidth: '100%' }}>
            <PaloPositionTable<OrderApprovalItem>
                items={items}
                columns={columns}
                getRowKey={item => item.id}
                readonly={true}
                quantityOf={item => item.quantity}
                amountOf={item => item.totalNetPrice}
                currency={currency}
            />
        </Box>
    );
};
